import { useMemo } from 'react';
import { View } from 'foundations-components/components/View';
import getLang from '../../utils/getLang';
import { InteractiveEducationLayout } from '../../features/interactiveEducation/components/InteractiveEducationLayout';
import { useInteractiveEducation } from '../../features/interactiveEducation/hooks/useInteractiveEducation';
import { useGenerateAsyncSummarization } from '../../features/interactiveEducation/hooks/useGenerateAsyncSummarization';
import { useUserIndustry } from '../../features/interactiveEducation/hooks/useUserIndustry';
import { extractVideoMetadata, buildInteractiveContentTrackingProps } from '../../features/interactiveEducation/utils/contentUtils';
import { VideoPlayerWithControls } from '../../features/video';
import { usePageTitle } from '../../hooks/usePageTitle';
import { jsx as _jsx } from "react/jsx-runtime";
export const LessonContentWithInteractiveEducation = ({
  lesson,
  lessonContentId,
  lessonQuizProps,
  onAdvanceContent,
  videoContent
}) => {
  var _lessonQuizProps$capt, _lessonQuizProps$capt2;
  usePageTitle({
    i18nKey: 'learningCenter.dynamicPageTitles.lessonContent',
    options: {
      contentName: videoContent === null || videoContent === void 0 ? void 0 : videoContent.name
    },
    skip: !(videoContent !== null && videoContent !== void 0 && videoContent.name)
  });
  const lessonId = Number(lesson.id);
  const language = lesson.language || getLang();
  const {
    contentPortalId,
    professorId,
    videoId
  } = extractVideoMetadata(videoContent);

  // Combine lesson and videoContent data for tracking props
  const trackingPropsSource = useMemo(() => videoContent ? Object.assign({}, videoContent, {
    hsSoftwareFilters: lesson.hsSoftwareFilters,
    subCategories: lesson.subCategories,
    useCases: lesson.useCases
  }) : undefined, [videoContent, lesson]);
  const {
    label: industry
  } = useUserIndustry();
  const interactiveContentTrackingProps = useMemo(() => buildInteractiveContentTrackingProps({
    content: trackingPropsSource,
    contentType: 'LESSON',
    contentId: lessonContentId || lessonId,
    professorId,
    industry,
    visibility: lesson.visibility
  }), [trackingPropsSource, lessonContentId, lessonId, professorId, industry, lesson.visibility]);
  const {
    displayInteractiveEducation,
    handleDisplayInteractiveEducation,
    interactiveEducationEnabled
  } = useInteractiveEducation({
    videoId,
    hasCaptions: (_lessonQuizProps$capt = (_lessonQuizProps$capt2 = lessonQuizProps.captionsExistMap) === null || _lessonQuizProps$capt2 === void 0 ? void 0 : _lessonQuizProps$capt2[String(lessonContentId)]) !== null && _lessonQuizProps$capt !== void 0 ? _lessonQuizProps$capt : false,
    interactiveContentTrackingProps
  });
  const {
    summary,
    correlationId: summaryCorrelationId,
    summaryError,
    summaryLoading,
    refetchSummary,
    videoDuration
  } = useGenerateAsyncSummarization({
    primaryContentId: lessonId,
    contentType: 'LESSON',
    lessonContentId
  });
  const interactiveEducationProps = {
    handleTriggerAsyncQuizGeneration: lessonQuizProps.regenerateQuiz,
    generateQuizError: lessonQuizProps.generateQuizError,
    generateQuizLoading: lessonQuizProps.generateQuizLoading,
    onRegenerateSingleVideoQuiz: lessonQuizProps.regenerateSingleVideoQuiz,
    summary,
    summaryCorrelationId,
    summaryError,
    summaryLoading,
    refetchSummary,
    videoDuration: videoDuration !== null && videoDuration !== void 0 ? videoDuration : undefined
  };
  return /*#__PURE__*/_jsx("div", {
    "data-contentid": lessonContentId,
    "data-test-id": "stored-content-container",
    children: /*#__PURE__*/_jsx(InteractiveEducationLayout, {
      contentPortalId: contentPortalId,
      contentType: "LESSON",
      displayInteractiveEducation: displayInteractiveEducation,
      interactiveContentTrackingProps: interactiveContentTrackingProps,
      interactiveEducationEnabled: interactiveEducationEnabled,
      interactiveEducationProps: interactiveEducationProps,
      lesson: lesson,
      lessonContentId: lessonContentId,
      primaryContentId: lessonId,
      videoId: videoId,
      videoTitle: videoContent.name,
      children: /*#__PURE__*/_jsx(View, {
        borderColor: "border-core-subtle",
        borderRadius: "300",
        borderStyle: "solid",
        borderWidth: "100",
        overflow: "hidden",
        children: /*#__PURE__*/_jsx(VideoPlayerWithControls, {
          additionalTracking: interactiveContentTrackingProps,
          contentId: lessonContentId.toString(),
          displayInteractiveEducation: displayInteractiveEducation,
          enableAdaptToIndustry: true,
          enableQuizMe: true,
          enableSummarize: true,
          handleDisplayInteractiveEducation: handleDisplayInteractiveEducation,
          interactiveEducationEnabled: interactiveEducationEnabled,
          isPubliclyVisible: lesson.visibility === 'PUBLIC',
          language: language,
          lessonId: lessonId.toString(),
          lessonName: lesson.name,
          onFinish: onAdvanceContent
        })
      })
    })
  });
};
export default LessonContentWithInteractiveEducation;